import { isAxiosError } from 'axios';
import { apiClient as api } from '@plerionhq/design-system';
import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { AccessGrant, ListGrantsResponse } from '../types';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

const BASE_PATH = '/api/core/access/grants';

// The API's error body: `{ errors: [{ code, message }] }`.
type ApiError = { code?: string; message?: string };

function firstApiError(error: unknown): ApiError | undefined {
  if (!isAxiosError(error)) {
    return undefined;
  }
  const errors: unknown = error.response?.data?.errors;
  return Array.isArray(errors) ? (errors[0] as ApiError) : undefined;
}

/**
 * Toast for a failed review write.
 *
 * The refusals here are decisions the reviewer has to act on, not transient
 * failures — most of all GrantChangedSinceReview, which means the permissions
 * moved under them and the review date cannot simply be pushed out. Its message
 * says what to do instead, so it is shown verbatim and left up long enough to
 * read; a generic "could not save" would just invite a retry that fails the
 * same way.
 */
function reportUpdateFailure(
  t: TFunction<'accessGrants'>,
  error: unknown,
): void {
  const apiError = firstApiError(error);
  if (apiError?.message) {
    toast.error(apiError.message, {
      duration: apiError.code === 'GrantChangedSinceReview' ? 10_000 : 6000,
    });
    return;
  }
  toast.error(t('couldNotSaveYourChange'));
}

// The editable review fields. Every key is optional: a single inline cell edit
// sends just the one field it changed, while the slide-over Audit tab may send
// several. `null` clears a field. Trust is machine-derived and not settable here.
export type UpdateGrantBody = {
  reviewDecision?: AccessGrant['reviewDecision'];
  grantee?: string | null;
  reviewComment?: string | null;
  nextReviewAt?: string | null;
};

type UpdateGrantVariables = { id: string } & UpdateGrantBody;

type GrantsInfiniteData = InfiniteData<ListGrantsResponse>;

// Patch a single grant in-place across every cached access-grants list page.
function patchGrantInPages(
  data: GrantsInfiniteData,
  id: string,
  patch: UpdateGrantBody,
): GrantsInfiniteData {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: page.data.map((grant) =>
        grant.id === id
          ? {
              ...grant,
              ...(patch.reviewDecision !== undefined && {
                reviewDecision: patch.reviewDecision,
              }),
              ...(patch.grantee !== undefined && {
                grantee: patch.grantee,
              }),
              ...(patch.reviewComment !== undefined && {
                reviewComment: patch.reviewComment,
              }),
              ...(patch.nextReviewAt !== undefined && {
                nextReviewAt: patch.nextReviewAt,
              }),
            }
          : grant,
      ),
    })),
  };
}

// Replace a grant row wholesale with the server's response across every cached
// list page. Unlike the optimistic patch, this carries the server-derived
// fields (reviewHistory, reviewedBy/At) so a reopened slide-over shows fresh
// history without refetching the whole list.
function replaceGrantInPages(
  data: GrantsInfiniteData,
  updated: AccessGrant,
): GrantsInfiniteData {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: page.data.map((grant) =>
        grant.id === updated.id ? updated : grant,
      ),
    })),
  };
}

/**
 * Mutation for the grant review lifecycle: grantee, decision, comment, and
 * next-review date.
 *
 * Optimistically patches the cached list so inline edits feel instant, rolls
 * back on error, and invalidates the list + stats on settle so server-derived
 * fields (reviewHistory, reviewedBy/At) and counts stay accurate.
 */
export function useUpdateGrant() {
  const { t } = useTranslation('accessGrants');
  const queryClient = useQueryClient();

  return useMutation<
    AccessGrant,
    unknown,
    UpdateGrantVariables,
    { previous: [readonly unknown[], GrantsInfiniteData][] }
  >({
    mutationFn: async ({ id, ...body }) => {
      const res = await api.patch<{ data: AccessGrant }>(
        `${BASE_PATH}/${id}`,
        body,
      );
      return res.data.data;
    },
    onMutate: async ({ id, ...patch }) => {
      await queryClient.cancelQueries({ queryKey: ['access-grants'] });

      const previous = queryClient.getQueriesData<GrantsInfiniteData>({
        queryKey: ['access-grants'],
      });

      for (const [key, data] of previous) {
        if (data) {
          queryClient.setQueryData<GrantsInfiniteData>(
            key,
            patchGrantInPages(data, id, patch),
          );
        }
      }

      return {
        previous: previous.filter(
          (entry): entry is [readonly unknown[], GrantsInfiniteData] =>
            entry[1] !== undefined,
        ),
      };
    },
    onError: (error, _variables, context) => {
      // Roll back every list we optimistically touched.
      context?.previous.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      reportUpdateFailure(t, error);
    },
    onSuccess: (updated) => {
      // Write the server row (with its server-derived reviewHistory/reviewedBy/
      // At) into every cached list page. This keeps the caches correct without a
      // blanket refetch on every field edit — important during a bulk-review
      // session, where each blur would otherwise refetch the whole list. Grant
      // review fields don't feed the stats aggregate (total / external /
      // untrustedExternal / crossOrg), so stats need no invalidation either.
      queryClient.setQueriesData<GrantsInfiniteData>(
        { queryKey: ['access-grants'] },
        (data) => (data ? replaceGrantInPages(data, updated) : data),
      );
    },
  });
}
